/**
 * Stamp and Label Renderer
 * Canvas drawing functions for HVAC stamps and labels
 */

import { getStampById, STAMP_SIZE, LABEL_SIZE, LABEL_TYPES } from '../../constants/hvacStamps';

/**
 * Draw a single stamp on the canvas
 */
export function drawStamp(ctx, stamp, isSelected = false, scale = 1) {
  const stampDef = getStampById(stamp.stampType);
  if (!stampDef) return;

  const { x, y } = stamp.position;
  const rotation = stamp.rotation || 0;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotation * Math.PI) / 180);

  // Stamp dimensions
  const width = STAMP_SIZE.width;
  const height = STAMP_SIZE.height;
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  // Draw background
  ctx.fillStyle = stampDef.color;
  ctx.beginPath();
  roundRect(ctx, -halfWidth, -halfHeight, width, height, STAMP_SIZE.borderRadius);
  ctx.fill();

  // Draw border
  ctx.strokeStyle = isSelected ? '#fff' : 'rgba(0,0,0,0.3)';
  ctx.lineWidth = isSelected ? 2 : 1;
  ctx.beginPath();
  roundRect(ctx, -halfWidth, -halfHeight, width, height, STAMP_SIZE.borderRadius);
  ctx.stroke();

  // Draw text
  ctx.fillStyle = stampDef.textColor;
  ctx.font = `bold ${STAMP_SIZE.fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const lines = stampDef.label.split('\n');
  const lineHeight = STAMP_SIZE.fontSize + 2;
  const totalHeight = lines.length * lineHeight;
  const startY = -(totalHeight / 2) + lineHeight / 2;

  lines.forEach((line, i) => {
    ctx.fillText(line, 0, startY + i * lineHeight);
  });

  // Draw selection handles
  if (isSelected) {
    ctx.fillStyle = '#00c8ff';
    const handleSize = 6;
    const corners = [
      [-halfWidth, -halfHeight],
      [halfWidth, -halfHeight],
      [-halfWidth, halfHeight],
      [halfWidth, halfHeight],
    ];
    corners.forEach(([cx, cy]) => {
      ctx.fillRect(cx - handleSize / 2, cy - handleSize / 2, handleSize, handleSize);
    });

    // Rotation handle
    ctx.beginPath();
    ctx.moveTo(0, -halfHeight);
    ctx.lineTo(0, -halfHeight - 15);
    ctx.strokeStyle = '#00c8ff';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, -halfHeight - 15, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

/**
 * Draw all stamps
 */
export function drawAllStamps(ctx, stamps, selectedId = null, scale = 1) {
  if (!stamps || stamps.length === 0) return;

  stamps.forEach((stamp) => {
    drawStamp(ctx, stamp, stamp.id === selectedId, scale);
  });
}

/**
 * Draw a single label on the canvas
 */
export function drawLabel(ctx, label, isSelected = false, scale = 1) {
  const labelDef = LABEL_TYPES[label.type?.toUpperCase()] || LABEL_TYPES.CFM;
  const { x, y } = label.position;

  ctx.save();
  ctx.translate(x, y);

  // Calculate text dimensions
  const displayText = `${label.value} ${labelDef.suffix}`.trim();
  ctx.font = `bold ${LABEL_SIZE.fontSize}px Arial`;
  const textWidth = ctx.measureText(displayText).width;
  const boxWidth = Math.max(LABEL_SIZE.minWidth, textWidth + LABEL_SIZE.padding * 2);
  const boxHeight = LABEL_SIZE.height;
  const halfWidth = boxWidth / 2;
  const halfHeight = boxHeight / 2;

  // Draw arrow if enabled
  if (label.showArrow && label.arrowTarget) {
    const targetX = label.arrowTarget.x - x;
    const targetY = label.arrowTarget.y - y;

    ctx.strokeStyle = labelDef.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(halfWidth, 0);
    ctx.lineTo(targetX, targetY);
    ctx.stroke();

    // Arrow head
    const angle = Math.atan2(targetY, targetX - halfWidth);
    const arrowSize = 8;
    ctx.fillStyle = labelDef.color;
    ctx.beginPath();
    ctx.moveTo(targetX, targetY);
    ctx.lineTo(
      targetX - arrowSize * Math.cos(angle - Math.PI / 6),
      targetY - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      targetX - arrowSize * Math.cos(angle + Math.PI / 6),
      targetY - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fill();
  } else if (label.showArrow) {
    // Default arrow pointing right
    ctx.strokeStyle = labelDef.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(halfWidth, 0);
    ctx.lineTo(halfWidth + LABEL_SIZE.arrowLength, 0);
    ctx.stroke();

    // Arrow head
    ctx.fillStyle = labelDef.color;
    ctx.beginPath();
    ctx.moveTo(halfWidth + LABEL_SIZE.arrowLength + 8, 0);
    ctx.lineTo(halfWidth + LABEL_SIZE.arrowLength, -5);
    ctx.lineTo(halfWidth + LABEL_SIZE.arrowLength, 5);
    ctx.closePath();
    ctx.fill();
  }

  // Draw background
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.beginPath();
  roundRect(ctx, -halfWidth, -halfHeight, boxWidth, boxHeight, LABEL_SIZE.borderRadius);
  ctx.fill();

  // Draw border
  ctx.strokeStyle = isSelected ? '#fff' : labelDef.color;
  ctx.lineWidth = isSelected ? 2 : 1.5;
  ctx.beginPath();
  roundRect(ctx, -halfWidth, -halfHeight, boxWidth, boxHeight, LABEL_SIZE.borderRadius);
  ctx.stroke();

  // Draw text
  ctx.fillStyle = labelDef.color;
  ctx.font = `bold ${LABEL_SIZE.fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(displayText, 0, 0);

  // Draw selection handles
  if (isSelected) {
    ctx.fillStyle = '#00c8ff';
    const handleSize = 6;
    const corners = [
      [-halfWidth, -halfHeight],
      [halfWidth, -halfHeight],
      [-halfWidth, halfHeight],
      [halfWidth, halfHeight],
    ];
    corners.forEach(([cx, cy]) => {
      ctx.fillRect(cx - handleSize / 2, cy - handleSize / 2, handleSize, handleSize);
    });

    // Arrow target handle if arrow is shown
    if (label.showArrow) {
      const targetX = label.arrowTarget ? label.arrowTarget.x - x : halfWidth + LABEL_SIZE.arrowLength + 8;
      const targetY = label.arrowTarget ? label.arrowTarget.y - y : 0;
      ctx.fillStyle = '#ff9800';
      ctx.beginPath();
      ctx.arc(targetX, targetY, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  ctx.restore();
}

/**
 * Draw all labels
 */
export function drawAllLabels(ctx, labels, selectedId = null, scale = 1) {
  if (!labels || labels.length === 0) return;

  labels.forEach((label) => {
    drawLabel(ctx, label, label.id === selectedId, scale);
  });
}

/**
 * Draw duct size labels along ducts
 */
export function drawDuctSizeLabels(ctx, ducts, showLabels = true) {
  if (!showLabels || !ducts || ducts.length === 0) return;

  ducts.forEach((duct) => {
    if (!duct.points || duct.points.length < 2) return;

    // Find midpoint of duct
    const midIndex = Math.floor(duct.points.length / 2);
    const p1 = duct.points[midIndex - 1] || duct.points[0];
    const p2 = duct.points[midIndex] || duct.points[1];
    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;

    // Calculate angle
    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);

    ctx.save();
    ctx.translate(midX, midY);

    // Rotate text to follow duct, but keep readable
    let textAngle = angle;
    if (textAngle > Math.PI / 2 || textAngle < -Math.PI / 2) {
      textAngle += Math.PI;
    }
    ctx.rotate(textAngle);

    // Draw label
    const sizeText = duct.shape === 'flex' ? `${duct.size}"` : `${duct.size}"`;
    ctx.font = 'bold 10px Arial';
    const textWidth = ctx.measureText(sizeText).width;

    // Background
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(-textWidth / 2 - 3, -8, textWidth + 6, 14);

    // Text
    ctx.fillStyle = duct.type === 'supply' ? '#e65100' : '#1565c0';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(sizeText, 0, 0);

    ctx.restore();
  });
}

/**
 * Draw equipment labels
 */
export function drawEquipmentLabels(ctx, equipment, showLabels = true) {
  if (!showLabels || !equipment || equipment.length === 0) return;

  equipment.forEach((eq) => {
    const labelText = eq.label || formatEquipmentLabel(eq);
    if (!labelText) return;

    ctx.save();
    ctx.translate(eq.x, eq.y + 35); // Position below equipment

    ctx.font = 'bold 10px Arial';
    const textWidth = ctx.measureText(labelText).width;

    // Background
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(-textWidth / 2 - 4, -7, textWidth + 8, 14);

    // Border
    ctx.strokeStyle = '#66bb6a';
    ctx.lineWidth = 1;
    ctx.strokeRect(-textWidth / 2 - 4, -7, textWidth + 8, 14);

    // Text
    ctx.fillStyle = '#66bb6a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(labelText, 0, 0);

    ctx.restore();
  });
}

/**
 * Format equipment label from type and size
 */
function formatEquipmentLabel(equipment) {
  const typeLabels = {
    furnace: 'Furnace',
    'air-handler': 'Air Handler',
    'ac-condenser': 'AC Condenser',
    'heat-pump': 'Heat Pump',
    'mini-split': 'Mini Split',
    boiler: 'Boiler',
    'water-heater': 'Water Heater',
    thermostat: 'T-stat',
  };

  const type = typeLabels[equipment.type] || equipment.type;
  const size = equipment.size || '';

  return size ? `${size} ${type}` : type;
}

/**
 * Hit test for stamp
 */
export function hitTestStamp(stamp, point, tolerance = 5) {
  const stampDef = getStampById(stamp.stampType);
  if (!stampDef) return false;

  const { x, y } = stamp.position;
  const rotation = (stamp.rotation || 0) * Math.PI / 180;

  // Transform point to stamp's local coordinates
  const dx = point.x - x;
  const dy = point.y - y;
  const localX = dx * Math.cos(-rotation) - dy * Math.sin(-rotation);
  const localY = dx * Math.sin(-rotation) + dy * Math.cos(-rotation);

  const halfWidth = STAMP_SIZE.width / 2 + tolerance;
  const halfHeight = STAMP_SIZE.height / 2 + tolerance;

  return Math.abs(localX) <= halfWidth && Math.abs(localY) <= halfHeight;
}

/**
 * Hit test for label
 */
export function hitTestLabel(label, point, tolerance = 5) {
  const { x, y } = label.position;
  const labelDef = LABEL_TYPES[label.type?.toUpperCase()] || LABEL_TYPES.CFM;

  // Estimate box width
  const displayText = `${label.value} ${labelDef.suffix}`.trim();
  const estimatedWidth = Math.max(LABEL_SIZE.minWidth, displayText.length * 8 + LABEL_SIZE.padding * 2);
  const halfWidth = estimatedWidth / 2 + tolerance;
  const halfHeight = LABEL_SIZE.height / 2 + tolerance;

  const dx = point.x - x;
  const dy = point.y - y;

  // Check main label box
  if (Math.abs(dx) <= halfWidth && Math.abs(dy) <= halfHeight) {
    return { hit: true, part: 'body' };
  }

  // Check arrow target handle
  if (label.showArrow && label.arrowTarget) {
    const targetDx = point.x - label.arrowTarget.x;
    const targetDy = point.y - label.arrowTarget.y;
    if (Math.abs(targetDx) <= 8 && Math.abs(targetDy) <= 8) {
      return { hit: true, part: 'arrow' };
    }
  }

  return { hit: false };
}

/**
 * Draw preview of stamp being placed
 */
export function drawStampPreview(ctx, stampType, position, rotation = 0) {
  const stampDef = getStampById(stampType);
  if (!stampDef) return;

  ctx.save();
  ctx.globalAlpha = 0.6;

  drawStamp(ctx, {
    stampType,
    position,
    rotation,
  }, false);

  ctx.restore();
}

/**
 * Draw preview of label being placed
 */
export function drawLabelPreview(ctx, labelConfig, position) {
  ctx.save();
  ctx.globalAlpha = 0.6;

  drawLabel(ctx, {
    ...labelConfig,
    position,
    id: 'preview',
  }, false);

  ctx.restore();
}

/**
 * Helper: Draw rounded rectangle
 */
function roundRect(ctx, x, y, width, height, radius) {
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
}
